import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Package, RotateCw } from 'lucide-react';
import ProductCodeBadge from '../../components/ProductCodeBadge';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const STATUS = {
  'Pending Acceptance': 'bg-zinc-700/50 text-zinc-300',
  'Not Accepted': 'bg-zinc-700/50 text-zinc-300',
  'Accepted': 'bg-blue-900/40 text-blue-300',
  'In Production': 'bg-amber-900/40 text-amber-300',
  'Packing': 'bg-indigo-900/40 text-indigo-300',
  'Dispatched': 'bg-green-900/40 text-green-300',
  'Delivered': 'bg-green-900/40 text-green-300',
  'Rejected': 'bg-red-900/40 text-red-300',
};

export default function PortalOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [reorderBusy, setReorderBusy] = useState(null);

  const load = () => {
    setLoading(true);
    axios.get(`${API}/portal/orders`, { withCredentials: true })
      .then(r => setOrders(r.data || []))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const reorder = async (o) => {
    if (!window.confirm(`Place the same order again?\n\n${(o.items || []).length} item(s), ₹${(o.total_amount || 0).toLocaleString('en-IN')}`)) return;
    setReorderBusy(o.id);
    try {
      const res = await axios.post(`${API}/portal/orders/${o.id}/reorder`, {}, { withCredentials: true });
      toast.success(`Re-order placed — ${res.data.items_count} item(s) submitted for admin acceptance`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to re-order'); }
    finally { setReorderBusy(null); }
  };

  return (
    <div className="space-y-4">
      <h1 className="font-heading text-2xl font-semibold text-zinc-50">Your Orders</h1>
      {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
      {!loading && orders.length === 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-10 text-center">
          <Package size={28} className="text-zinc-600 mx-auto mb-3" />
          <div className="text-zinc-400 text-sm">No orders yet</div>
        </div>
      )}
      <div className="space-y-2">
        {orders.map(o => {
          const open = expanded === o.id;
          return (
            <div key={o.id} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden" data-testid={`portal-order-${o.id}`}>
              <button onClick={() => setExpanded(open ? null : o.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-zinc-800/40 text-left">
                <div>
                  <div className="text-zinc-50 text-sm font-semibold">{o.invoice_number || o.voucher_no || o.id.slice(0, 10)}</div>
                  <div className="text-zinc-500 text-[11px]">
                    {o.created_at?.slice(0, 10)} · {o.items?.length || 0} item{o.items?.length !== 1 ? 's' : ''}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${STATUS[o.status] || 'bg-zinc-700 text-zinc-300'}`}>{o.status}</span>
                  <span className="text-amber-400 font-mono text-sm font-semibold">{fmt(o.total_amount)}</span>
                  <ChevronDown size={14} className={`text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
                </div>
              </button>
              {open && (
                <div className="px-4 py-3 border-t border-zinc-800 space-y-3 bg-zinc-950/50">
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-zinc-500">Payment</div>
                      <div className="text-zinc-200 mt-0.5">{o.payment_status} · Paid {fmt(o.paid_amount)}</div>
                    </div>
                    <div>
                      <div className="text-zinc-500">Dispatched</div>
                      <div className="text-zinc-200 mt-0.5">{o.dispatched_at?.slice(0, 10) || '—'}</div>
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500 text-xs mb-1.5">Items (with Batch IDs)</div>
                    <div className="space-y-1.5">
                      {(o.items || []).map((it, i) => (
                        <div key={i} className="bg-zinc-900 rounded-lg p-2.5 border border-zinc-800">
                          <div className="flex items-center justify-between">
                            <div className="text-zinc-100 text-xs font-medium flex items-center gap-1.5 flex-wrap">
                              <ProductCodeBadge name={it.product_name} code={it.product_code} />
                              {it.product_name}
                            </div>
                            <div className="text-zinc-400 text-xs font-mono">{it.qty} {it.unit} · {fmt(it.price)}</div>
                          </div>
                          {it.batch_ids?.length > 0 && (
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {it.batch_ids.map(bid => (
                                <span key={bid} className="font-mono text-[10px] bg-amber-500/10 border border-amber-500/30 text-amber-300 px-1.5 py-0.5 rounded" data-testid={`batch-id-${bid}`}>
                                  {bid}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                      {(o.items || []).length === 0 && <div className="text-zinc-600 text-xs italic">Details pending</div>}
                    </div>
                  </div>
                  {/* Re-order CTA */}
                  {(o.items || []).length > 0 && (
                    <div className="flex justify-end pt-2 border-t border-zinc-800">
                      <button onClick={() => reorder(o)} disabled={reorderBusy === o.id}
                        data-testid={`reorder-${o.id}`}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-black rounded-lg text-xs font-semibold">
                        <RotateCw size={12} /> {reorderBusy === o.id ? 'Placing…' : 'Order Again'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
